import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import {
	obscure,
	restore,
	isObscured,
	shouldBlockShortcut,
	shouldObscureOnKeyDown,
	isPrintScreenKey,
} from '../../../shared/MediaProtection';

const RESTORE_DELAY_MS = 500;
const CAPTURE = true;

const isInsideSelector = (selector, node) => {
	if (!selector || !node || !node.closest) {
		return false;
	}

	return Boolean(node.closest(selector));
};

const MediaProtectionGuard = ({ active, focusContainmentSelector }) => {
	const restoreTimeoutRef = useRef(null);

	useEffect(() => {
		if (!active) {
			restore();
			return undefined;
		}

		const clearRestoreTimeout = () => {
			if (restoreTimeoutRef.current) {
				clearTimeout(restoreTimeoutRef.current);
				restoreTimeoutRef.current = null;
			}
		};

		const scheduleRestore = () => {
			clearRestoreTimeout();
			restoreTimeoutRef.current = setTimeout(restore, RESTORE_DELAY_MS);
		};

		const isInsidePreview = (node) => isInsideSelector(focusContainmentSelector, node);

		const shouldObscureOutsidePreview = (node) => (
			Boolean(focusContainmentSelector && node && !isInsidePreview(node))
		);

		const restoreIfInsidePreview = (node) => {
			if (isObscured() && isInsidePreview(node)) {
				clearRestoreTimeout();
				restore();
			}
		};

		const handleBlur = () => {
			window.requestAnimationFrame(() => {
				if (document.hidden) {
					return;
				}

				if (typeof document.hasFocus === 'function' && !document.hasFocus()) {
					obscure();
					return;
				}

				if (isInsidePreview(document.activeElement)) {
					return;
				}

				if (!focusContainmentSelector) {
					obscure();
				}
			});
		};

		const handleFocus = () => {
			if (!document.hidden && !focusContainmentSelector) {
				clearRestoreTimeout();
				restore();
			}
		};

		const handleVisibilityChange = () => {
			if (document.hidden) {
				obscure();
			} else {
				clearRestoreTimeout();
				restore();
			}
		};

		const handleFocusIn = () => {
			if (!focusContainmentSelector) {
				return;
			}

			window.requestAnimationFrame(() => {
				if (document.hidden) {
					return;
				}

				if (isInsidePreview(document.activeElement)) {
					restoreIfInsidePreview(document.activeElement);
					return;
				}

				if (shouldObscureOutsidePreview(document.activeElement)) {
					obscure();
				}
			});
		};

		const handlePointerDown = (event) => {
			if (!focusContainmentSelector) {
				return;
			}

			if (isInsidePreview(event.target)) {
				restoreIfInsidePreview(event.target);
				return;
			}

			if (shouldObscureOutsidePreview(event.target)) {
				obscure();
			}
		};

		const handleKeyDown = (event) => {
			if (shouldObscureOnKeyDown(event)) {
				obscure();
				event.preventDefault();
				event.stopPropagation();
			}

			if (shouldBlockShortcut(event)) {
				event.preventDefault();
			}
		};

		const handleKeyUp = (event) => {
			if (isPrintScreenKey(event)) {
				scheduleRestore();
			}
		};

		window.addEventListener('blur', handleBlur);
		window.addEventListener('focus', handleFocus);
		document.addEventListener('visibilitychange', handleVisibilityChange);
		document.addEventListener('focusin', handleFocusIn, CAPTURE);
		document.addEventListener('mousedown', handlePointerDown, CAPTURE);
		document.addEventListener('touchstart', handlePointerDown, CAPTURE);
		window.addEventListener('keydown', handleKeyDown, CAPTURE);
		window.addEventListener('keyup', handleKeyUp, CAPTURE);
		document.addEventListener('keydown', handleKeyDown, CAPTURE);
		document.addEventListener('keyup', handleKeyUp, CAPTURE);

		return () => {
			clearRestoreTimeout();
			window.removeEventListener('blur', handleBlur);
			window.removeEventListener('focus', handleFocus);
			document.removeEventListener('visibilitychange', handleVisibilityChange);
			document.removeEventListener('focusin', handleFocusIn, CAPTURE);
			document.removeEventListener('mousedown', handlePointerDown, CAPTURE);
			document.removeEventListener('touchstart', handlePointerDown, CAPTURE);
			window.removeEventListener('keydown', handleKeyDown, CAPTURE);
			window.removeEventListener('keyup', handleKeyUp, CAPTURE);
			document.removeEventListener('keydown', handleKeyDown, CAPTURE);
			document.removeEventListener('keyup', handleKeyUp, CAPTURE);
			restore();
		};
	}, [active, focusContainmentSelector]);

	return null;
};

MediaProtectionGuard.propTypes = {
	active: PropTypes.bool,
	focusContainmentSelector: PropTypes.string,
};

MediaProtectionGuard.defaultProps = {
	active: false,
	focusContainmentSelector: null,
};

export default MediaProtectionGuard;
